import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  getSourcePdfSignedUrl,
  getSubmission,
  listSubmissionPages,
  updatePageReview,
} from '../lib/db.ts';
import type { ReviewDecision, SubmissionPage } from '../lib/types.ts';
import {
  Card,
  ErrorBox,
  Loader,
  MetaRow,
  NavBtn,
  mono,
} from '../components/takeoff/primitives.tsx';
import { COLORS } from '../components/takeoff/colors.ts';
import { CategoryPicker } from '../components/review/CategoryPicker.tsx';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

const FLAGGED = new Set(['low_confidence', 'unclassified', 'needs_ocr']);

interface PageState {
  jobName: string;
  pages: SubmissionPage[];
  sourceUrl: string;
  error: string | null;
}

export function Review() {
  const { id } = useParams<{ id: string }>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const cancelRenderRef = useRef<(() => void) | null>(null);
  const decideRef = useRef<(d: ReviewDecision) => Promise<void>>(() => Promise.resolve());
  const categoryInputRef = useRef<HTMLInputElement>(null);
  const categoryEmptyRef = useRef(true);

  const [pageState, setPageState] = useState<PageState | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfReady, setPdfReady] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [categoryInput, setCategoryInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [renderStatus, setRenderStatus] = useState<'idle' | 'rendering' | 'error'>('idle');
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const flagged = pageState?.pages.filter((p) => FLAGGED.has(p.status ?? '')) ?? [];
  const current = flagged[currentIdx] ?? null;

  // ── Load submission + pages + signed URL ─────────────────────────────────
  useEffect(() => {
    if (!id) return;
    let active = true;

    async function load() {
      try {
        const [submission, pages] = await Promise.all([
          getSubmission(id!),
          listSubmissionPages(id!),
        ]);
        if (!active) return;
        if (!submission) {
          setPageState({ jobName: '', pages: [], sourceUrl: '', error: 'Submission not found.' });
          return;
        }
        const sourceUrl = await getSourcePdfSignedUrl(id!, submission.source_pdf);
        if (active) setPageState({ jobName: submission.job_name, pages, sourceUrl, error: null });
      } catch (err: unknown) {
        if (active) {
          setPageState({ jobName: '', pages: [], sourceUrl: '', error: err instanceof Error ? err.message : 'Load failed' });
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    void load();
    return () => { active = false; };
  }, [id]);

  // ── Load PDF document ────────────────────────────────────────────────────
  useEffect(() => {
    const url = pageState?.sourceUrl;
    if (!url) return;
    let active = true;

    async function loadDoc() {
      try {
        const doc = await pdfjsLib.getDocument({ url }).promise;
        if (!active) { void doc.destroy(); return; }
        pdfDocRef.current?.destroy();
        pdfDocRef.current = doc;
        setPdfReady(true);
      } catch {
        // error handled by render effect
      }
    }

    void loadDoc();
    return () => {
      active = false;
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;
      setPdfReady(false);
    };
  }, [pageState?.sourceUrl]);

  // ── Track container size so scale calc has accurate dims ────────────────
  const flaggedLen = pageState?.pages.filter((p) => FLAGGED.has(p.status ?? '')).length ?? 0;
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, flaggedLen]);

  // ── Render current page ──────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfReady || !current || !canvasRef.current) return;
    if (containerSize.w === 0 || containerSize.h === 0) return;

    cancelRenderRef.current?.();
    let cancelled = false;
    cancelRenderRef.current = () => { cancelled = true; };

    setRenderStatus('rendering');

    const pageNum = current.page_index + 1;

    async function render() {
      try {
        const doc = pdfDocRef.current;
        if (!doc || cancelled) return;

        const page = await doc.getPage(pageNum);
        if (cancelled) { page.cleanup(); return; }

        const containerWidth = containerSize.w - 32;
        const baseVp = page.getViewport({ scale: 1 });
        // Fit to width; user scrolls vertically. Standard PDF-viewer behavior.
        const scale = Math.max(0.5, Math.min(containerWidth / baseVp.width, 3));
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current!;
        // pdfjs-dist v5 does NOT auto-size the canvas; it renders into whatever
        // canvas.width/height already are (default 300x150 → clipped output).
        // Size for DPR before render, then pin CSS dims to logical viewport.
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined;

        const task = page.render({ canvas, viewport, transform });
        cancelRenderRef.current = () => { cancelled = true; task.cancel(); page.cleanup(); };

        await task.promise;
        if (!cancelled) setRenderStatus('idle');
      } catch {
        if (!cancelled) setRenderStatus('error');
      }
    }

    void render();
  }, [pdfReady, current?.id, containerSize.w, containerSize.h]);

  // ── Sync category input ───────────────────────────────────────────────────
  useEffect(() => {
    setCategoryInput(current?.review_human_category ?? current?.category ?? '');
  }, [current?.id]);

  // ── Decision handler ─────────────────────────────────────────────────────
  const decide = useCallback(async (decision: ReviewDecision) => {
    if (!current || saving) return;
    // Reject requires a human-supplied category — that's the corpus signal we
    // need for grading. If empty, focus the picker instead of committing.
    if (decision === 'reject' && categoryInput.trim() === '') {
      categoryInputRef.current?.focus();
      return;
    }
    const humanCat =
      decision === 'approve' ? categoryInput.trim() || current.category || null
      : decision === 'reject' ? categoryInput.trim() || null
      : null;

    setSaving(true);
    try {
      await updatePageReview(current.id, decision, humanCat);
      setPageState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) =>
            p.id === current.id ? { ...p, review_decision: decision, review_human_category: humanCat } : p
          ),
        };
      });
      // Advance to next unreviewed
      setCurrentIdx((idx) => {
        const nextUnreviewed = flagged.findIndex((p, i) => i > idx && p.review_decision == null);
        if (nextUnreviewed !== -1) return nextUnreviewed;
        if (idx < flagged.length - 1) return idx + 1;
        return idx;
      });
    } finally {
      setSaving(false);
    }
  }, [current, saving, categoryInput, flagged]);

  decideRef.current = decide;
  categoryEmptyRef.current = categoryInput.trim() === '';

  // ── Keyboard shortcuts (stable listener, mutable ref) ───────────────────
  useEffect(() => {
    const flaggedRef = { len: 0 };

    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key.toLowerCase()) {
        case 'a': void decideRef.current('approve'); break;
        case 'r':
          // R commits reject when the picker has a value; otherwise focus it
          // so the reviewer can supply a category. decide() also enforces
          // this guard for click-driven reject.
          if (categoryEmptyRef.current) {
            e.preventDefault();
            categoryInputRef.current?.focus();
          } else {
            void decideRef.current('reject');
          }
          break;
        case 's': void decideRef.current('skip'); break;
        case 'arrowleft':
          e.preventDefault();
          setCurrentIdx((i) => Math.max(0, i - 1));
          break;
        case 'arrowright':
          e.preventDefault();
          setCurrentIdx((i) => Math.min(Math.max(0, flaggedRef.len - 1), i + 1));
          break;
      }
    }

    // Keep len current via a side-channel — updates without re-registering
    const unsub = setInterval(() => { flaggedRef.len = flagged.length; }, 50);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); clearInterval(unsub); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync flaggedRef via a separate effect that doesn't touch the listener
  useEffect(() => { /* just force deptrack — actual sync above */ }, [flagged.length]);

  // ── Early returns ─────────────────────────────────────────────────────────
  if (loading) return <Loader text="Loading review…" />;

  if (pageState?.error) {
    return (
      <ErrorBox message={pageState.error} />
    );
  }

  if (flagged.length === 0) {
    return (
      <div>
        <BackLink id={id!} label={pageState?.jobName ?? 'Back'} />
        <div style={{ marginTop: 40, color: '#B8BDD6', fontSize: 14 }}>
          No flagged pages to review for this submission.
        </div>
      </div>
    );
  }

  const reviewed = flagged.filter((p) => p.review_decision !== null).length;
  const allDone = reviewed === flagged.length;

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <BackLink id={id!} label={pageState?.jobName ?? 'Back'} />
        <div style={{ flex: 1 }} />
        <span style={mono(11, '#525870')}>{reviewed} / {flagged.length} reviewed</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NavBtn disabled={currentIdx === 0} onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}>←</NavBtn>
          <span style={mono(12, '#B8BDD6')}>{currentIdx + 1} / {flagged.length}</span>
          <NavBtn disabled={currentIdx >= flagged.length - 1} onClick={() => setCurrentIdx((i) => Math.min(flagged.length - 1, i + 1))}>→</NavBtn>
        </div>
      </div>

      {allDone && (
        <div style={{ padding: '10px 14px', background: 'rgba(0,232,122,0.08)', border: '1px solid rgba(0,232,122,0.3)', borderRadius: 8, color: '#00E87A', fontSize: 13, marginBottom: 16 }}>
          All {flagged.length} flagged pages reviewed.{' '}
          <Link to={`/app/jobs/${id}`} style={{ color: '#00E87A', fontWeight: 600 }}>Back to job →</Link>
        </div>
      )}

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
        {/* PDF canvas */}
        <div
          ref={containerRef}
          style={{
            background: '#0D0F1C',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 10,
            padding: 16,
            height: 'calc(100vh - 180px)',
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          {!pdfReady && <span style={mono(13, '#525870')}>Loading PDF…</span>}
          {pdfReady && renderStatus === 'error' && <span style={mono(13, '#FF6B7A')}>Page render failed</span>}
          <canvas
            ref={canvasRef}
            style={{
              display: pdfReady ? 'block' : 'none',
              minWidth: 0,
              opacity: renderStatus === 'rendering' ? 0.3 : 1,
              transition: 'opacity 180ms ease',
            }}
          />
        </div>

        {/* Sidebar */}
        <div style={{ position: 'sticky', top: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {current && (
            <>
              {/* Page metadata */}
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15 }}>
                    Page {current.page_index + 1}
                  </span>
                  {current.page_label && <span style={mono(10, '#525870')}>{current.page_label}</span>}
                </div>
                <MetaRow label="STATUS" value={current.status ?? '—'} warn={current.status !== 'classified'} />
                <MetaRow label="CATEGORY" value={current.category ?? '—'} />
                {current.confidence != null && (
                  <MetaRow label="CONFIDENCE" value={`${Math.round(current.confidence * 100)}%`} />
                )}
                {current.vision_applied && <MetaRow label="VISION" value="applied" />}
                {current.review_decision && (
                  <MetaRow
                    label="DECISION"
                    value={current.review_decision}
                    warn={current.review_decision === 'reject'}
                    ok={current.review_decision === 'approve'}
                  />
                )}
              </Card>

              {/* Category picker — required for reject (corpus signal) */}
              <Card>
                <label style={{ display: 'block', ...mono(10, '#525870'), letterSpacing: '0.1em', marginBottom: 6 }}>
                  CATEGORY {categoryInput.trim() === '' && <span style={{ color: '#FF6B7A' }}>· required to reject</span>}
                </label>
                <CategoryPicker
                  ref={categoryInputRef}
                  value={categoryInput}
                  onChange={setCategoryInput}
                  required
                />
              </Card>

              {/* Decision buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <DecisionBtn label="Approve" hint="A" color="#00E87A" active={current.review_decision === 'approve'} disabled={saving} onClick={() => void decide('approve')} />
                <DecisionBtn
                  label="Reject"
                  hint="R"
                  color="#FF6B7A"
                  active={current.review_decision === 'reject'}
                  disabled={saving || categoryInput.trim() === ''}
                  onClick={() => void decide('reject')}
                />
                <DecisionBtn label="Skip" hint="S" color="#525870" active={current.review_decision === 'skip'} disabled={saving} onClick={() => void decide('skip')} />
              </div>

              <div style={{ textAlign: 'center', ...mono(11, '#525870') }}>A · R · S · ← →</div>

              {/* Queue mini-map */}
              <Card>
                <div style={{ ...mono(10, '#525870'), letterSpacing: '0.1em', marginBottom: 8 }}>QUEUE</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {flagged.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      title={`p${p.page_index + 1} · ${p.category ?? 'unclassified'} · ${p.review_decision ?? 'pending'}`}
                      onClick={() => setCurrentIdx(i)}
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        padding: 0,
                        cursor: 'pointer',
                        border: i === currentIdx ? '2px solid #F0F2FF' : '1px solid rgba(255,255,255,0.1)',
                        background:
                          p.review_decision === 'approve' ? 'rgba(0,232,122,0.55)'
                          : p.review_decision === 'reject' ? 'rgba(255,107,122,0.55)'
                          : p.review_decision === 'skip' ? 'rgba(82,88,112,0.55)'
                          : 'rgba(255,255,255,0.08)',
                      }}
                    />
                  ))}
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Small components ─────────────────────────────────────────────────────────

function BackLink({ id, label }: { id: string; label: string }) {
  return (
    <Link
      to={`/app/jobs/${id}`}
      style={{ color: '#525870', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', textDecoration: 'none' }}
    >
      ← {label}
    </Link>
  );
}

// Review-specific decision button (color comes from caller per-action).
function DecisionBtn({
  label,
  hint,
  color,
  active,
  disabled,
  onClick,
}: {
  label: string;
  hint: string;
  color: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        borderRadius: 7,
        border: `1px solid ${active ? color : COLORS.border}`,
        background: active ? `color-mix(in srgb, ${color} 12%, transparent)` : COLORS.bg,
        color: active ? color : COLORS.textMuted,
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'border 100ms, background 100ms, color 100ms',
        width: '100%',
      }}
    >
      <span>{label}</span>
      <kbd
        style={{
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          color: active ? color : COLORS.textDim,
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid ${COLORS.border}`,
          borderRadius: 3,
          padding: '2px 6px',
        }}
      >
        {hint}
      </kbd>
    </button>
  );
}
